import { useMemo, useState } from 'react';
import { useTheme } from 'styled-components';
import { useQuery } from '@apollo/client';

import { TooltipNewIcon, Typography } from '@libs/components';
import {
  formatBalance,
  getAmountPrefixAndColor,
  getDecimalFromPercentage,
  getPercentageWithSymbol,
} from '@libs/helpers';
import { CurrencyType, WalletInfoItem } from '@libs/types';
import { TimeRangeEnum } from '@libs/backend-common';

import { InteractiveChart, FavoriteAssetIndicator } from 'src/components';

import { getExchangeRateHistoryForPeriod } from 'src/gql';
import { useApiResponse, useTranslation } from 'src/hooks';
import { CoinMarketCapAsset } from 'src/types';

import { getAlternativeAssetLabel } from 'src/helpers';
import { CoinDetailsCharts } from '../../constants/constants';
import { useCoinDetailsTabs } from '../../hooks/useCoinDetailsTabs';

import { NO_DETAILS_ASSETS } from '../../CoinDetailsPage';
import { PERIOD_TRANSLATIONS } from './translationKeys';
import {
  AssetBalanceWrapper,
  AssetDescriptionWrapper,
  AssetIcon,
  AssetTitleDescWrapper,
  AssetTitleWrapper,
  AssetWrapper,
  ChartWrapper,
  CmcRatingWrapper,
  ContentWrapper,
  CustomFavoriteAssetIndicator,
  CustomTabs,
  CustomTooltip,
  FavoriteAssetLaptopWrapper,
  FavoriteAssetMobileWrapper,
  // CustomToolTipIcon,
  Separator,
  TabsWrapper,
} from './styles';

export type PossibleTimeRangeValue =
  | TimeRangeEnum.ONE_DAY
  | TimeRangeEnum.ONE_WEEK
  | TimeRangeEnum.ONE_MONTH
  | TimeRangeEnum.ONE_YEAR;

type CoinDetailsChartProps = {
  asset: WalletInfoItem;
  currencySymbol: string;
  chartIsVisible?: boolean;
  assetInfo?: CoinMarketCapAsset;
  activeTabValue: PossibleTimeRangeValue;
  handlePeriodTabChange: (val: number | string) => void;
  refetch: () => void;
};
const CoinDetailsChart: React.FC<CoinDetailsChartProps> = ({
  asset,
  currencySymbol,
  chartIsVisible = true,
  activeTabValue,
  assetInfo,
  handlePeriodTabChange,
  refetch,
}) => {
  const { t } = useTranslation();
  const { colors } = useTheme();

  const { chartChangeTabs, periodTabs, periodEnumValues } = useCoinDetailsTabs(
    asset.code
  );

  const periodActiveTab = periodEnumValues[activeTabValue];

  const [chartChangeActiveTabId, setChartChangeActiveTabId] = useState<
    string | number
  >(chartChangeTabs[0].id);

  const res = useQuery(getExchangeRateHistoryForPeriod, {
    variables: {
      record: {
        baseAssetCode: asset.code,
        quoteAssetCode: CurrencyType.USD,
        days: activeTabValue,
      },
    },
  });

  const { response } = useApiResponse(res);

  const marketCapChartData = useMemo(() => {
    if (!response) return [];
    if (chartChangeActiveTabId === CoinDetailsCharts.PRICE_CHART) {
      return response.getExchangeRateHistoryForPeriod;
    }
    return response.getExchangeRateHistoryForPeriod.map((i) => ({
      ...i,
      amount: i.marketCap,
    }));
  }, [response, chartChangeActiveTabId]);

  const iconRate = assetInfo?.rate || 0;
  const formattedRate =
    iconRate < 1 ? iconRate : formatBalance(assetInfo?.rate, undefined, 2);

  const change = getDecimalFromPercentage(assetInfo?.changePercentage);

  const { prefix, changeColor, absoluteAmount } = getAmountPrefixAndColor(
    colors,
    change
  );

  const handleChartTabChange = (id: string | number) => {
    setChartChangeActiveTabId(id);
  };

  const isUnstableCoin = !NO_DETAILS_ASSETS.includes(asset.code);

  return (
    <ContentWrapper>
      <AssetDescriptionWrapper>
        {asset && (
          <AssetWrapper>
            <FavoriteAssetLaptopWrapper>
              <FavoriteAssetIndicator
                isFavorite={asset.isFavorite}
                assetCode={asset.code}
                refetch={refetch}
              />
            </FavoriteAssetLaptopWrapper>
            <AssetIcon src={asset.iconUrl} />
            <AssetTitleDescWrapper>
              <AssetTitleWrapper>
                <Typography type="subHeadline" fontFamily="primaryBold">
                  {asset.name}
                </Typography>

                {assetInfo?.cmcRating && (
                  <CmcRatingWrapper>
                    <Typography type="bodyButton" fontFamily="primaryMedium">
                      #{assetInfo?.cmcRating}
                    </Typography>
                  </CmcRatingWrapper>
                )}
                <CustomTooltip
                  icon={<TooltipNewIcon />}
                  desc={t('coin_details_coin_tooltip', {
                    order: assetInfo?.cmcRating || '',
                  })}
                />

                {/* <CustomToolTipIcon /> */}
              </AssetTitleWrapper>

              <Typography
                type="bodyButton"
                fontFamily="primaryMedium"
                color={colors.text.secondary}
              >
                {getAlternativeAssetLabel(asset.code)}
              </Typography>
            </AssetTitleDescWrapper>
          </AssetWrapper>
        )}

        {isUnstableCoin && (
          <AssetBalanceWrapper>
            <Typography type="headline" fontFamily="primaryBold">
              {formattedRate + currencySymbol}
            </Typography>
            <Typography
              type="subHeadline"
              fontFamily="primaryBold"
              color={changeColor}
            >
              {prefix}
              {getPercentageWithSymbol(absoluteAmount)} (
              {t(PERIOD_TRANSLATIONS[activeTabValue])})
            </Typography>
          </AssetBalanceWrapper>
        )}
      </AssetDescriptionWrapper>
      <FavoriteAssetMobileWrapper>
        <CustomFavoriteAssetIndicator
          isFavorite={asset.isFavorite}
          assetCode={asset.code}
          refetch={refetch}
          shouldHaveLabel
        />
      </FavoriteAssetMobileWrapper>
      {chartIsVisible && (
        <>
          <Separator />
          <TabsWrapper>
            <CustomTabs
              tabs={chartChangeTabs}
              handleTabClick={handleChartTabChange}
              activeTab={chartChangeActiveTabId}
            />
          </TabsWrapper>
          <ChartWrapper>
            <InteractiveChart
              data={marketCapChartData}
              dateFormat={periodActiveTab.dateFormat}
              customMargin={{ top: 0, bottom: 0, left: 0, right: 0 }}
              tooltipIcon={asset.iconUrl}
              tooltipTitle={asset.code}
              shouldHideYAxis
            />
          </ChartWrapper>
          <TabsWrapper>
            <CustomTabs
              tabs={periodTabs}
              handleTabClick={handlePeriodTabChange}
              activeTab={activeTabValue}
            />
          </TabsWrapper>
        </>
      )}
    </ContentWrapper>
  );
};

export default CoinDetailsChart;
